import { articleTemplateApi, ArticleTemplateDetailRes } from '@/axios/articleTemplate';
import DeleteIcon from '@/assets/imgs/deleteTime.svg?react';
import ArticleTypeSelector from '@/components/Form/ArticleTypeSelector';
import MediaBox from '@/components/MediaBox';
import SVGIcon from '@/components/SVGIcon';
import EllipsisText from '@/components/Table/EllipsisText';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { Button, Flex, InputNumber, Table, Tooltip, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import React, { useEffect, useMemo, useRef, useState } from 'react';

export const defaultCombinations: ArticleTemplateDetailRes['combinations'] = [
  {
    articleCount: 1,
    items: [],
  },
];

export const validateTemplateCombinations = (combinations: ArticleTemplateDetailRes['combinations']) => {
  const throwWithMessage = (text: string) => {
    message.error(text);
    throw new Error(text);
  };
  if (!combinations.length) {
    throwWithMessage('请至少添加一个媒体组合');
  }
  for (const combination of combinations) {
    if (!combination.items || combination.items.length === 0) {
      throwWithMessage('每个媒体组合的已选媒体列表不能为空');
    }
    if (!combination.articleCount || combination.articleCount < 1) {
      throwWithMessage('每个媒体组合的发文数量必须大于0');
    }
    for (const item of combination.items || []) {
      if (!item.articleTypeId) {
        throwWithMessage('已选媒体列表中的文章类型不能为空');
      }
    }
  }
};

const formatPriceRange = (range?: { minPrice?: number; maxPrice?: number } | null) => {
  const minPrice = range?.minPrice || 0;
  const maxPrice = range?.maxPrice || 0;
  if (!range) {
    return '-';
  }
  if (minPrice === maxPrice) {
    return `${minPrice}元`;
  }
  return `${minPrice}-${maxPrice}元`;
};

const isAbnormalMediaStatus = (record: any) => {
  const status = String(record?.status || '').toUpperCase();
  const hasReason = Boolean(record?.reason);
  if (hasReason) {
    return true;
  }
  if (!status) {
    return false;
  }
  return status === 'CHANGE' || status === 'DISABLED';
};

const ArticleTemplateCombinationEditor = ({
  value,
  onChange,
  listMaxHeight = 400,
  mediaBoxScrollY = 140,
  selectedTableScrollY = 220,
}: {
  value: ArticleTemplateDetailRes['combinations'];
  onChange: (nextValue: ArticleTemplateDetailRes['combinations']) => void;
  listMaxHeight?: number;
  mediaBoxScrollY?: number;
  selectedTableScrollY?: number;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const combinationsRef = useRef<HTMLDivElement>(null);
  const currentCombination = useMemo(() => value[currentIndex], [value, currentIndex]);

  useEffect(() => {
    if (!value.length) {
      onChange([...defaultCombinations]);
      setCurrentIndex(0);
      return;
    }
    if (currentIndex > value.length - 1) {
      setCurrentIndex(value.length - 1);
    }
  }, [value, currentIndex, onChange]);

  const { data: priceRanges } = useRequest(
    () => {
      const articleCount = currentCombination?.articleCount || 0;
      const mediaIds = currentCombination?.items?.map((item) => item.mediaId) || [];
      if (articleCount === 0 || mediaIds.length === 0) {
        return Promise.resolve(null);
      }
      return articleTemplateApi.combinationPrice({
        articleCount,
        mediaIds,
      });
    },
    {
      refreshDeps: [currentIndex, currentCombination?.articleCount, currentCombination?.items],
      onSuccess: (result) => {
        if (!result || !currentCombination) {
          return;
        }
        const nextCombinations = [...value];
        nextCombinations[currentIndex] = {
          ...nextCombinations[currentIndex],
          minPrice: result.minPrice,
          maxPrice: result.maxPrice,
        };
        onChange(nextCombinations);
      },
    }
  );

  const sumPriceRanges = useMemo(() => {
    let minPrice = 0;
    let maxPrice = 0;
    value.forEach((combination) => {
      minPrice += combination.minPrice || 0;
      maxPrice += combination.maxPrice || 0;
    });
    return { minPrice, maxPrice };
  }, [value]);

  const updateCurrentCombination = (
    updater: (prev: NonNullable<typeof currentCombination>) => typeof currentCombination
  ) => {
    if (!currentCombination) {
      message.warning('请先选择一个媒体组合');
      return;
    }
    const nextCombinations = [...value];
    nextCombinations[currentIndex] = updater(currentCombination);
    onChange(nextCombinations);
  };

  const columns: ColumnsType<any> = [
    {
      title: '媒体名称',
      dataIndex: 'mediaName',
      ellipsis: true,
      render: (mediaName: string, record: any) => {
        const abnormal = isAbnormalMediaStatus(record);
        const reason = record?.reason || (record?.status ? `状态异常：${record.status}` : '媒体状态异常');
        return (
          <Flex align="center" gap={6}>
            <EllipsisText text={mediaName} />
            {abnormal && (
              <Tooltip title={reason}>
                <ExclamationCircleFilled style={{ color: '#ff4d4f', cursor: 'pointer' }} />
              </Tooltip>
            )}
          </Flex>
        );
      },
    },
    {
      title: '价格',
      dataIndex: 'price',
      width: 80,
    },
    {
      title: '文章类型',
      dataIndex: 'articleTypeId',
      render: (articleTypeId: string, record: any, index: number) => (
        <ArticleTypeSelector
          style={{ width: '200px' }}
          value={articleTypeId}
          defaultOptions={
            record?.articleTypeId
              ? [
                  {
                    value: record.articleTypeId,
                    label: record.articleTypeName || record.articleTypeId,
                  },
                ]
              : []
          }
          onChange={(nextTypeId) => {
            updateCurrentCombination((prev) => {
              const newItems = [...(prev.items || [])];
              newItems[index] = {
                ...newItems[index],
                articleTypeId: nextTypeId as string,
              };
              return {
                ...prev,
                items: newItems,
              };
            });
          }}
        />
      ),
    },
    {
      title: '操作',
      dataIndex: 'operate',
      width: 80,
      render: (_: any, _record: any, index: number) => (
        <a
          onClick={() => {
            updateCurrentCombination((prev) => {
              const newItems = (prev.items || []).filter((_, i) => i !== index);
              return {
                ...prev,
                items: newItems,
                articleCount: newItems.length || 1,
              };
            });
          }}
        >
          移除
        </a>
      ),
    },
  ];

  return (
    <>
      <div>媒体组合</div>
      <Flex gap={10}>
        <Flex
          style={{
            background: 'rgb(247, 248, 250)',
            padding: '12px',
            borderRadius: '4px',
            width: '300px',
          }}
          vertical
          gap={8}
        >
          <Flex ref={combinationsRef} vertical gap={8} style={{ overflowY: 'auto', maxHeight: `${listMaxHeight}px` }}>
            {value.map((combination, index) => (
              <Flex
                gap={8}
                align="center"
                key={`combination-${index}`}
                style={{
                  marginBottom: '8px',
                }}
              >
                <Flex
                  gap={8}
                  align="center"
                  justify="space-between"
                  style={{
                    flex: 1,
                    padding: '8px',
                    background: '#fff',
                    borderRadius: '4px',
                    border: currentIndex === index ? '2px solid #1890ff' : '2px solid transparent',
                  }}
                  onClick={() => {
                    setCurrentIndex(index);
                  }}
                >
                  <div>媒体组合{index + 1}</div>
                  <div style={{ color: '#666A80' }}>{combination.articleCount}篇文章</div>
                  <div style={{ color: '#666A80' }}>
                    {formatPriceRange({ minPrice: combination.minPrice, maxPrice: combination.maxPrice })}
                  </div>
                </Flex>
                <SVGIcon
                  onClick={() => {
                    const nextCombinations = [...value];
                    nextCombinations.splice(index, 1);
                    onChange(nextCombinations);
                    if (currentIndex === index) {
                      setCurrentIndex(0);
                    } else if (currentIndex > index) {
                      setCurrentIndex((prev) => prev - 1);
                    }
                  }}
                  icon={<DeleteIcon />}
                />
              </Flex>
            ))}
          </Flex>
          <Button
            type="link"
            onClick={() => {
              onChange([
                ...value,
                {
                  articleCount: 1,
                  items: [],
                },
              ]);
              setCurrentIndex(value.length);
              setTimeout(() => {
                if (combinationsRef.current) {
                  combinationsRef.current.scrollTop = combinationsRef.current.scrollHeight;
                }
              });
            }}
          >
            添加
          </Button>
          <div
            style={{
              marginTop: 'auto',
            }}
          >
            价格： {formatPriceRange(sumPriceRanges)}
          </div>
        </Flex>

        <Flex
          vertical
          gap={10}
          style={{
            background: 'rgb(247, 248, 250)',
            padding: '12px',
            borderRadius: '4px',
            flex: 1,
            minWidth: 0,
          }}
        >
          <div>组合详情(媒体组合{currentIndex + 1})</div>
          <MediaBox
            scroll={{ x: 720, y: mediaBoxScrollY }}
            size="small"
            onAdd={(item) => {
              updateCurrentCombination((prev) => ({
                ...prev,
                items: [
                  ...(prev.items || []),
                  {
                    articleTypeId: undefined,
                    mediaId: item.id || item.mediaId,
                    mediaName: item.mediaName,
                    price: item.price,
                  },
                ],
              }));
            }}
          />
          <div>已选媒体列表</div>
          <Table
            columns={columns}
            dataSource={currentCombination ? currentCombination.items : []}
            scroll={{ y: selectedTableScrollY }}
            pagination={false}
            size="small"
          />
          <Flex align="center" gap={8}>
            <div style={{ flexShrink: 0 }}>发文数量</div>
            <InputNumber
              value={currentCombination?.articleCount}
              onChange={(val) => {
                updateCurrentCombination((prev) => ({
                  ...prev,
                  articleCount: val || 1,
                }));
              }}
              style={{ width: '100px' }}
              min={1}
            />
            <span
              style={{
                color: '#888',
                fontSize: '12px',
              }}
            >
              当发文数量小于已选媒体列表中的个数时，从已选媒体列表中随机选择【发文数量】个媒体不重复发文；相等时每个媒体各发一篇；大于时每个媒体发最大公因数篇，剩下的随机
            </span>
          </Flex>
          <div>价格： {formatPriceRange(priceRanges)}</div>
        </Flex>
      </Flex>
    </>
  );
};

export default ArticleTemplateCombinationEditor;
